import { reactive } from "vue";

// Définition d'un type générique pour mieux typer l'objet réactif
type AnyObject = Record<string, any>;

function useObject<T extends AnyObject = AnyObject>(obj: T = {} as T) {
  // Correction de la vérification du type d'objet
  if (Object.prototype.toString.call(obj) !== "[object Object]") {
    throw new Error("Un objet est attendu. Valeur passée : " + String(obj));
  }
  let _keys = Object.keys(obj);
  const reactiveObject = reactive(obj) as T;

  /**
   * Remplace intégralement le contenu de l'objet réactif
   * (toutes les clefs sont supprimées puis ajoutées depuis newObject)
   */
  const doChangeObject = function (newObject: T = {} as T) {
    // Capture l'état précédent pour calculer les différences
    const previous: AnyObject = {};
    _keys.forEach((key) => {
      previous[key] = reactiveObject[key];
    });

    // Réinitialise totalement l'objet courant
    _keys.forEach(key => delete reactiveObject[key]);

    // N'ajoute que les propriétés dont la valeur a changé
    const changedEntries = Object.entries(newObject).filter(([k, v]) => previous[k] !== v);
    const filtered: AnyObject = Object.fromEntries(changedEntries);
    Object.assign(reactiveObject, filtered);
    _keys = Object.keys(filtered);
  };

  /**
   * Ajoute ou écrase des propriétés dans l'objet réactif courant
   */
  const doAddToObject = function (newObject: Partial<T> = {}) {
    Object.assign(reactiveObject, newObject);
    // (pas de .value, car 'reactive' ne fournit pas de .value, c'est 'ref' qui le fait)
  };

  return {
    reactiveObject,
    doChangeObject,
    doAddToObject,
  };
}

export default useObject;
